import React from 'react';

export default function HowItWorks({ steps, branding }) {
  const primaryColor = branding?.primary_color || '#1e40af';
  const accentColor = branding?.secondary_color || '#AEFFE3';

  return (
    <section className="relative bg-gradient-to-b from-white to-gray-50 py-24 px-6 overflow-hidden">
      {/* Gradient background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 right-0 w-96 h-96 bg-blue-100/30 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-100/20 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <div className="inline-block mb-4 px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-50 rounded-full border border-blue-200">
            <span className="text-sm font-semibold text-blue-900">
              Our Process
            </span>
          </div>
          <h2 className="text-5xl font-bold mb-4" style={{ color: primaryColor }}>
            How It Works
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Our streamlined 4-step process delivers results without the complexity
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, idx) => (
            <div key={idx} className="relative group">
              {/* Connecting line (desktop only) */}
              {idx < steps.length - 1 && (
                <div className="hidden lg:block absolute top-12 left-full w-full h-0.5 bg-gradient-to-r from-blue-300 to-transparent -z-10" />
              )}

              {/* Glass morphism card */}
              <div className="relative">
                {/* Glow effect on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-purple-500/10 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />

                {/* Card */}
                <div className="relative bg-white/80 backdrop-blur-xl rounded-3xl p-8 border border-gray-200/50 shadow-xl hover:shadow-2xl transition-all hover:-translate-y-2">
                  {/* Step number with gradient */}
                  <div className="relative mb-6">
                    <div
                      className="w-16 h-16 rounded-2xl flex items-center justify-center text-2xl font-bold text-white mx-auto shadow-lg"
                      style={{ background: `linear-gradient(135deg, ${primaryColor}, ${accentColor}40)` }}
                    >
                      {idx + 1}
                    </div>

                    {/* Decorative circle */}
                    <div
                      className="absolute -top-2 -right-2 w-6 h-6 rounded-full opacity-50"
                      style={{ backgroundColor: accentColor }}
                    />
                  </div>

                  <h3 className="text-xl font-bold mb-4 text-center" style={{ color: primaryColor }}>
                    {step.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-center">
                    {step.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA below steps */}
        <div className="mt-16 text-center">
          <div className="inline-block bg-white/80 backdrop-blur-md rounded-2xl p-8 border border-gray-200/50 shadow-xl max-w-2xl">
            <p className="text-lg text-gray-700 mb-4">
              <strong className="text-blue-900">Average implementation time:</strong> 30-45 days from consultation to full deployment
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
              <svg className="w-5 h-5" style={{ color: accentColor }} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>No disruption to your current operations</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
